import { Component, Input, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { MarkdownService } from '../services/markdown.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-markdown-viewer',
  templateUrl: './markdown-viewer.component.html',
  styleUrls: ['./markdown-viewer.component.scss'],
  standalone: false
})
export class MarkdownViewerComponent implements OnChanges, AfterViewInit {
  @Input() content: string = '';
  @ViewChild('markdownContainer') markdownContainer: ElementRef<HTMLDivElement>;

  renderedHtml: SafeHtml = '';

  constructor(
    private markdownService: MarkdownService,
    private sanitizer: DomSanitizer
  ) {}

  ngAfterViewInit(): void {
    this.renderContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) {
      this.renderContent();
    }
  }

  @HostListener('click', ['$event'])
  onContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a[data-anchor]') as HTMLAnchorElement;

    if (anchor) {
      event.preventDefault();
      const anchorId = anchor.getAttribute('data-anchor');
      this.scrollToAnchor(anchorId);
    }
  }

  private scrollToAnchor(anchorId: string): void {
    if (!anchorId || !this.markdownContainer?.nativeElement) return;

    // Try to find element by id or by heading text converted to id format
    const container = this.markdownContainer.nativeElement;
    let targetElement = container.querySelector(`#${CSS.escape(anchorId)}`);

    // If not found by id, search headings by text content (common in markdown TOCs)
    if (!targetElement) {
      const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
      for (const heading of Array.from(headings)) {
        const headingId = heading.textContent?.toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        if (headingId === anchorId || `-${headingId}` === anchorId) {
          targetElement = heading;
          break;
        }
      }
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private renderContent(): void {
    if (this.content) {
      const html = this.markdownService.renderToHtml(this.content);
      this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    } else {
      this.renderedHtml = '';
    }
  }
}
